import * as React from 'react';
import { LocalDatasheetTabDto } from '../../types/tab-dto';
import { DatasheetLocalGetDto } from '../../types/datasheet';
import { DatasheetElement, DatasheetElementId } from '@modules/datasheets/types/datasheet-element';
import { hasCopiedTabInStorage } from '@modules/datasheets/utils/copied-tab-storage';

type Props = {
  copiedDatasheetElementsFromTab: Record<DatasheetElementId, DatasheetElement>;
  hasCopiedTab?: boolean;
  currentTabId?: LocalDatasheetTabDto['id'];
  datasheet: DatasheetLocalGetDto;
  onDatasheetChange: (datasheet: DatasheetLocalGetDto) => void;
};

export const useTabsController = (props: Props) => {
  const { onDatasheetChange, datasheet, currentTabId, copiedDatasheetElementsFromTab, hasCopiedTab = false } = props;

  const [hasCopiedInStorage, setHasCopiedInStorage] = React.useState<boolean>(() => hasCopiedTabInStorage());

  React.useEffect(() => {
    // Keep local state in sync with copied tab data in sessionStorage (cross-datasheet copy/paste).
    const syncFromStorage = () => {
      setHasCopiedInStorage(hasCopiedTabInStorage());
    };

    syncFromStorage();

    const intervalId = window.setInterval(syncFromStorage, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  const handleTabOrderChange = React.useCallback((tabs: DatasheetLocalGetDto['tabs']) => {
    onDatasheetChange({
      ...datasheet,
      tabs 
    });
  }, [datasheet, onDatasheetChange]);

  const handleTabRename = React.useCallback((tabName: string, tabId?: LocalDatasheetTabDto['id']) => {
    const idToRename = tabId !== undefined && tabId !== null ? tabId : currentTabId;
    onDatasheetChange({
      ...datasheet,
      tabs: datasheet.tabs.map(tab => tab.id == idToRename ? { ...tab, name: tabName } : tab)
    });
  }, [onDatasheetChange, datasheet, currentTabId]);

  const pastingOfTabElementsAllowed = React.useMemo(() => {
    // Allow pasting if a tab has been copied (even if empty)
    // Check sessionStorage first (for cross-datasheet paste)
    // hasCopiedTab flag indicates that copy was explicitly called
    // Also check if copiedDatasheetElementsFromTab has a tab object (it might be a tab object or elements record)
    const hasCopiedDataInStorage = hasCopiedInStorage;
    const hasCopiedData = hasCopiedTab || 
      hasCopiedDataInStorage ||
      (copiedDatasheetElementsFromTab && (
        Object.keys(copiedDatasheetElementsFromTab).length > 0 ||
        (copiedDatasheetElementsFromTab as any).id !== undefined // Check if it's a tab object
      ));
    return hasCopiedData;
  }, [copiedDatasheetElementsFromTab, hasCopiedTab, hasCopiedInStorage]);

  return {
    pastingOfTabElementsAllowed,
    handleTabRename,
    handleTabOrderChange
  };
};